import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import { ReportLoading } from "@/components/leasing/ReportState";
import Pagination from "@/components/Pagination";
import FuelTransactionWizard from "@/components/fuel/FuelTransactionWizard";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Upload, Search } from "lucide-react";

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function FuelTransactions() {
  const [month, setMonth] = useState(currentYm());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [wizard, setWizard] = useState(false);
  const pageSize = 50;

  const { loading, data, reload } = useAsyncData(async () => {
    const res = await api.get("/fuel/transactions", { params: { month: `${month}`, search, page, page_size: pageSize } });
    return res.data;
  }, [month, search, page]);

  const txns = data?.transactions || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Lună (LL-AAAA)</Label>
            <MonthInput value={month} onChange={(v) => { setMonth(v); setPage(1); }} className="w-40" data-testid="fuel-tx-month" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-slate-500">Caută</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="fuel-tx-search" placeholder="Plăcuță, produs, stație…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 w-64" />
            </div>
          </div>
        </div>
        <Button data-testid="fuel-import-tx-btn" onClick={() => setWizard(true)} className="bg-slate-900 text-white gap-2"><Upload className="h-4 w-4" /> Import tranzacții</Button>
      </div>

      {loading && !data ? <ReportLoading label="Se încarcă tranzacțiile…" /> : (
        <>
          <div className="border rounded-md overflow-auto bg-card max-h-[65vh]">
            <table className="w-full text-sm jh-table" data-testid="fuel-tx-table">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2">Nr înmatriculare</th>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Produs</th>
                  <th className="text-right px-3 py-2">Cantitate</th>
                  <th className="text-right px-3 py-2">Valoare brută</th>
                  <th className="text-right px-3 py-2">TVA</th>
                  <th className="text-right px-3 py-2">Valoare netă</th>
                  <th className="text-left px-3 py-2">Stație</th>
                  <th className="text-right px-3 py-2">Km</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="hover:bg-amber-50/40" data-testid={`fuel-tx-row-${t.id}`}>
                    <td className="px-3 py-1.5 font-medium">{t.plate_raw}</td>
                    <td className="px-3 py-1.5 tabular">{fmtDate(t.data_tranzactie)}</td>
                    <td className="px-3 py-1.5">{t.produs}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(t.cantitate)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(t.valoare_bruta)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(t.tva)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(t.valoare_neta)}</td>
                    <td className="px-3 py-1.5">{t.statie}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(t.km, 0)}</td>
                  </tr>
                ))}
                {txns.length === 0 && !loading && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Nicio tranzacție pentru această lună. Importați exportul OMV.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} label="tranzacții" testid="fuel-tx-pagination" />
        </>
      )}

      {wizard && <FuelTransactionWizard open onOpenChange={(v) => !v && setWizard(false)} onDone={(m) => { if (m) setMonth(m); reload(); }} defaultMonth={month} />}
    </div>
  );
}
